import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChevronRight, Calculator, Thermometer, Zap, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';

const DimensioneringPage = () => {
  const { language } = useLanguage();
  const [step, setStep] = useState(1);
  const [appType, setAppType] = useState('');
  const [power, setPower] = useState('');
  const [deltaT, setDeltaT] = useState('');
  const [bufferTime, setBufferTime] = useState('');
  const [result, setResult] = useState(null);

  const calculateVolume = () => {
    // Simplified calculation: V = (P × t) / (ρ × Cp × ΔT)
    // Where: P=power(kW), t=time(h), ρ=density(kg/L), Cp=specific heat, ΔT=temp diff
    const P = parseFloat(power);
    const t = parseFloat(bufferTime) / 60; // Convert minutes to hours
    const dT = parseFloat(deltaT);
    
    if (!P || !t || !dT) return;
    
    // For water: ρ=1 kg/L, Cp=4.18 kJ/(kg·K) = 1.16 Wh/(kg·K)
    const volume = (P * t * 1000) / (1 * 1.16 * dT); // Liters
    
    // Match to products
    let recommendedProduct = null;
    let alternatives = [];
    
    if (volume <= 500) {
      recommendedProduct = { model: 'FL6000', volume: 300, type: 'PN3' };
      alternatives = [{ model: 'FL6000', volume: 500, type: 'PN6' }];
    } else if (volume <= 1000) {
      recommendedProduct = { model: 'FL6000', volume: 750, type: 'PN6' };
      alternatives = [{ model: 'FL6000', volume: 1000, type: 'PN6' }];
    } else if (volume <= 2000) {
      recommendedProduct = { model: 'FL6100', volume: 1500, type: 'PN6' };
      alternatives = [{ model: 'FL6100', volume: 2000, type: 'PN6' }];
    } else {
      recommendedProduct = { model: 'FL6200', volume: 3000, type: 'PN10' };
      alternatives = [{ model: 'FL6200', volume: 4000, type: 'PN10' }];
    }
    
    setResult({
      calculatedVolume: Math.round(volume),
      recommended: recommendedProduct,
      alternatives: alternatives,
      appType: appType
    });
    
    setStep(4);
  };

  const resetCalculator = () => {
    setStep(1);
    setAppType('');
    setPower('');
    setDeltaT('');
    setBufferTime('');
    setResult(null);
  };

  return (
    <div className="bg-white min-h-screen pt-24">
      <SEO 
        title="Dimensionera Ackumulatortank | Flowingo AB"
        description="Intelligent dimensioneringsverktyg för ackumulatortankar. Få rätt tankstorlek baserat på dina systemdata."
        language={language}
        schema={[
          generateBreadcrumbSchema([
            { name: 'Hem', url: 'https://flowingo.se' },
            { name: 'Dimensionering', url: 'https://flowingo.se/dimensionering' },
          ]),
        ]}
      />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-stone-500 mb-8">
          <Link to="/" className="hover:text-stone-900">Hem</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-stone-900">Dimensionering</span>
        </nav>

        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-stone-100 px-4 py-2 rounded-full mb-6">
            <Calculator className="w-4 h-4 text-[#321a27]" />
            <span className="text-sm font-medium text-stone-700">Intelligent dimensionering</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-6">
            Hitta rätt ackumulatortank
          </h1>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto">
            Svara på några enkla frågor om ditt system så guidar vi dig till rätt tankmodell och volym.
          </p>
        </div>

        {/* Progress */}
        <div className="mb-12">
          <div className="flex justify-between mb-2">
            {[1, 2, 3, 4].map(s => (
              <div key={s} className={`flex-1 h-1 ${s <= step ? 'bg-[#321a27]' : 'bg-stone-200'} ${s < 4 ? 'mr-2' : ''}`} />
            ))}
          </div>
          <div className="flex justify-between text-xs text-stone-500">
            <span className={step >= 1 ? 'text-[#321a27] font-medium' : ''}>Applikation</span>
            <span className={step >= 2 ? 'text-[#321a27] font-medium' : ''}>Systemdata</span>
            <span className={step >= 3 ? 'text-[#321a27] font-medium' : ''}>Beräkning</span>
            <span className={step >= 4 ? 'text-[#321a27] font-medium' : ''}>Resultat</span>
          </div>
        </div>

        {/* Step 1: Application Type */}
        {step === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { id: 'heating', icon: Thermometer, title: 'Värmesystem', desc: 'Buffertank för värmepumpar, pannor eller solenergi' },
              { id: 'cooling', icon: Thermometer, title: 'Kylsystem', desc: 'Buffertank för köldbärare och kylapplikationer' },
              { id: 'dhw', icon: Zap, title: 'Tappvarmvatten', desc: 'Varmvattenberedning via slingor eller elpatron' }
            ].map(app => (
              <button
                key={app.id}
                onClick={() => { setAppType(app.id); setStep(2); }}
                className="p-8 border-2 border-stone-200 hover:border-[#321a27] transition-all group text-left"
              >
                <app.icon className="w-12 h-12 text-stone-400 group-hover:text-[#321a27] mb-4" />
                <h3 className="text-xl font-semibold text-stone-900 mb-2">{app.title}</h3>
                <p className="text-stone-600 text-sm">{app.desc}</p>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: System Data */}
        {step === 2 && (
          <div className="max-w-2xl mx-auto bg-stone-50 p-8 border border-stone-200">
            <h2 className="text-2xl font-bold text-stone-900 mb-6">Systemdata</h2>
            <div className="space-y-6">
              <div>
                <Label className="text-stone-700 mb-2 block">Systemeffekt (kW)</Label>
                <Input 
                  type="number" 
                  value={power}
                  onChange={(e) => setPower(e.target.value)}
                  placeholder="t.ex. 15"
                  className="bg-white border-stone-300"
                />
                <p className="text-xs text-stone-500 mt-1">Värmekällans max effekt</p>
              </div>
              
              <div>
                <Label className="text-stone-700 mb-2 block">Temperaturdifferens ΔT (°C)</Label>
                <Input 
                  type="number" 
                  value={deltaT}
                  onChange={(e) => setDeltaT(e.target.value)}
                  placeholder="t.ex. 10"
                  className="bg-white border-stone-300"
                />
                <p className="text-xs text-stone-500 mt-1">Skillnad fram/retur</p>
              </div>
              
              <div>
                <Label className="text-stone-700 mb-2 block">Önskad bufferttid (minuter)</Label>
                <Input 
                  type="number" 
                  value={bufferTime}
                  onChange={(e) => setBufferTime(e.target.value)}
                  placeholder="t.ex. 30"
                  className="bg-white border-stone-300"
                />
                <p className="text-xs text-stone-500 mt-1">Hur länge ska tanken buffra</p>
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <Button 
                variant="outline" 
                onClick={() => setStep(1)}
                className="flex-1 border-stone-300"
              >
                Tillbaka
              </Button>
              <Button 
                onClick={() => setStep(3)}
                disabled={!power || !deltaT || !bufferTime}
                className="flex-1 bg-[#321a27] hover:bg-[#4a2d3d] text-white"
              >
                Nästa <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Confirm & Calculate */}
        {step === 3 && (
          <div className="max-w-2xl mx-auto bg-stone-50 p-8 border border-stone-200">
            <h2 className="text-2xl font-bold text-stone-900 mb-6">Sammanfattning</h2>
            <div className="space-y-4 mb-8">
              <div className="flex justify-between py-3 border-b border-stone-200">
                <span className="text-stone-600">Applikation:</span>
                <span className="font-medium text-stone-900">
                  {appType === 'heating' ? 'Värmesystem' : appType === 'cooling' ? 'Kylsystem' : 'Tappvarmvatten'}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-stone-200">
                <span className="text-stone-600">Effekt:</span>
                <span className="font-medium text-stone-900">{power} kW</span>
              </div>
              <div className="flex justify-between py-3 border-b border-stone-200">
                <span className="text-stone-600">Temperaturdifferens:</span>
                <span className="font-medium text-stone-900">{deltaT} °C</span>
              </div>
              <div className="flex justify-between py-3 border-b border-stone-200">
                <span className="text-stone-600">Bufferttid:</span>
                <span className="font-medium text-stone-900">{bufferTime} minuter</span>
              </div>
            </div>
            
            <div className="flex gap-4">
              <Button 
                variant="outline" 
                onClick={() => setStep(2)}
                className="flex-1 border-stone-300"
              >
                Tillbaka
              </Button>
              <Button 
                onClick={calculateVolume}
                className="flex-1 bg-[#321a27] hover:bg-[#4a2d3d] text-white"
              >
                Beräkna <Calculator className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Results */}
        {step === 4 && result && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-[#321a27] to-[#4a2d3d] text-white p-12 mb-8">
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle className="w-12 h-12" />
                <div>
                  <h2 className="text-3xl font-bold">Resultat</h2>
                  <p className="text-white/80">Baserat på dina systemdata</p>
                </div>
              </div>
              
              <div className="bg-white/10 backdrop-blur p-6 rounded">
                <p className="text-white/80 text-sm mb-2">Beräknad tankvolym:</p>
                <p className="text-5xl font-bold mb-1">{result.calculatedVolume}</p>
                <p className="text-white/80">liter</p>
              </div>
            </div>

            {/* Recommended Product */}
            <div className="bg-white border-2 border-[#321a27] p-8 mb-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="text-xs uppercase tracking-wider text-[#321a27] font-semibold">Rekommenderad</span>
                  <h3 className="text-3xl font-bold text-stone-900 mt-1">
                    {result.recommended.model} {result.recommended.volume}L
                  </h3>
                  <p className="text-stone-600 mt-2">{result.recommended.type}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-6 py-6 border-y border-stone-200">
                <div>
                  <p className="text-xs text-stone-500 mb-1">Volym</p>
                  <p className="text-xl font-bold text-stone-900">{result.recommended.volume}L</p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 mb-1">Tryckklass</p>
                  <p className="text-xl font-bold text-stone-900">{result.recommended.type}</p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 mb-1">Lämplighet</p>
                  <p className="text-xl font-bold text-stone-900">Optimal</p>
                </div>
              </div>
              
              <Link to={`/produkter?search=${result.recommended.model}`}>
                <Button className="w-full bg-[#321a27] hover:bg-[#4a2d3d] text-white py-6 text-lg">
                  Se produkt <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Alternatives */}
            {result.alternatives.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-stone-900 mb-4">Alternativa val</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {result.alternatives.map((alt, idx) => (
                    <div key={idx} className="bg-stone-50 p-6 border border-stone-200">
                      <h4 className="text-lg font-semibold text-stone-900 mb-2">
                        {alt.model} {alt.volume}L
                      </h4>
                      <p className="text-sm text-stone-600 mb-4">{alt.type}</p>
                      <Link to={`/produkter?search=${alt.model}`}>
                        <Button variant="outline" className="w-full border-stone-300 text-stone-700">
                          Se produkt
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button 
              variant="outline" 
              onClick={resetCalculator}
              className="w-full border-stone-300 text-stone-700"
            >
              Ny beräkning
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DimensioneringPage;
